/* eslint-disable no-param-reassign */
(() => {
  const DEBOUNCE_DELAY = 300;
  const INIT_DELAY = 100;
  const UPDATE_DELAY = 150;

  class CartShipping {
    constructor() {
      this.shippingCostElement = null;
      this.countryCode = this.getCountryCode();
      this.updateTimeout = null;
      this.isUpdating = false;
      this.init();
    }

    getCountryCode() {
      if (window.theme?.countryCode) {
        return window.theme.countryCode;
      }

      if (window.Shopify?.country) {
        return window.Shopify.country;
      }

      try {
        const cookieValue = document.cookie
          .split('; ')
          .find((row) => row.startsWith('localization='))
          ?.split('=')[1];
        if (cookieValue) {
          return cookieValue;
        }
      } catch (e) {
        // Silent fail
      }

      const hiddenInput = document.querySelector('input[name="country_code"]');
      if (hiddenInput?.value) {
        return hiddenInput.value;
      }

      return 'DE';
    }

    init() {
      this.findShippingElement();

      if (!this.shippingCostElement) {
        setTimeout(() => this.init(), INIT_DELAY);
        return;
      }

      this.updateShippingCost();
      this.observeCartFooter();
    }

    findShippingElement() {
      this.shippingCostElement = document.querySelector('.js-shipping-cost');
    }

    observeCartFooter() {
      const cartFooter = document.querySelector('.cart-footer');
      const cartDrawer = document.querySelector('cart-drawer');

      if (!cartFooter || !cartDrawer) {
        setTimeout(() => this.observeCartFooter(), INIT_DELAY);
        return;
      }

      const observer = new MutationObserver((mutations) => {
        const cartFooterReplaced = mutations.some((mutation) => {
          if (mutation.type !== 'childList') return false;

          return Array.from(mutation.addedNodes).some(
            (node) =>
              node.nodeType === 1 &&
              node.classList?.contains('cart-footer') &&
              node.querySelector('.js-shipping-cost')
          );
        });

        if (cartFooterReplaced) {
          setTimeout(() => {
            this.findShippingElement();
            this.countryCode = this.getCountryCode();
            if (this.shippingCostElement) {
              this.updateShippingCost();
            }
          }, UPDATE_DELAY);
        }
      });

      observer.observe(cartDrawer, {
        childList: true,
        subtree: false,
      });
    }

    async updateShippingCost() {
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }

      this.updateTimeout = setTimeout(async () => {
        if (this.isUpdating || !this.shippingCostElement) return;

        const cartFooter = this.shippingCostElement.closest('.cart-footer');
        if (cartFooter?.classList.contains('hidden')) {
          return;
        }

        if (!document.contains(this.shippingCostElement)) {
          this.findShippingElement();
          if (!this.shippingCostElement) return;
        }

        this.isUpdating = true;

        try {
          const cartResponse = await fetch(
            `${window.Shopify?.routes?.root || '/'}cart.js`
          );

          if (!cartResponse.ok) {
            this.setShippingCost(this.getFallbackText());
            return;
          }

          const cartData = await cartResponse.json();

          if (!cartData?.items?.length) {
            this.setShippingCost('0,00 €');
            return;
          }

          const shippingRates = await this.fetchShippingRates();

          if (shippingRates?.shipping_rates?.length > 0) {
            const cheapestRate = shippingRates.shipping_rates.reduce(
              (prev, current) => {
                const prevPrice = this.getPriceValue(prev);
                const currentPrice = this.getPriceValue(current);
                return prevPrice < currentPrice ? prev : current;
              }
            );

            const formattedPrice = this.getPriceInCents(cheapestRate);
            this.setShippingCost(formattedPrice);
          } else {
            this.setShippingCost(this.getFallbackText());
          }
        } catch (error) {
          this.setShippingCost(this.getFallbackText());
        } finally {
          this.isUpdating = false;
        }
      }, DEBOUNCE_DELAY);
    }

    getFallbackText() {
      return (
        window.theme?.translations?.cart?.shipping_calculated_at_checkout ||
        'werden beim Checkout berechnet'
      );
    }

    async fetchShippingRates() {
      try {
        const shippingAddress = this.getDefaultShippingAddress();
        const root = window.Shopify?.routes?.root || '/';

        if (!shippingAddress.country || !shippingAddress.zip) {
          return null;
        }

        const params = new URLSearchParams();
        params.append('shipping_address[country]', shippingAddress.country);
        params.append('shipping_address[zip]', shippingAddress.zip);

        if (shippingAddress.city) {
          params.append('shipping_address[city]', shippingAddress.city);
        }

        if (shippingAddress.province) {
          params.append('shipping_address[province]', shippingAddress.province);
        }

        const url = `${root}cart/shipping_rates.json?${params.toString()}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        });

        if (!response.ok) {
          return null;
        }

        return await response.json();
      } catch (error) {
        return null;
      }
    }

    getDefaultShippingAddress() {
      const countryCode = this.countryCode.toUpperCase();
      //Hardcoded Data for now, due to deadline. Could use metaobject in the future.
      const defaultAddresses = {
        DE: { zip: '10115', city: 'Berlin', province: 'BE' },
        AT: { zip: '1010', city: 'Vienna', province: 'W' },
        CH: { zip: '8001', city: 'Zurich', province: 'ZH' },
        FR: { zip: '75001', city: 'Paris', province: 'IDF' },
        IT: { zip: '00118', city: 'Rome', province: 'RM' },
        ES: { zip: '28001', city: 'Madrid', province: 'M' },
        NL: { zip: '1011', city: 'Amsterdam', province: 'NH' },
        BE: { zip: '1000', city: 'Brussels', province: 'BRU' },
        PL: { zip: '00001', city: 'Warsaw', province: 'MZ' },
        EG: { zip: '11511', city: 'Cairo', province: 'Cairo' },
        US: { zip: '10001', city: 'New York', province: 'NY' },
        GB: { zip: 'SW1A 1AA', city: 'London', province: 'ENG' },
        CA: { zip: 'M5H 2N2', city: 'Toronto', province: 'ON' },
        AU: { zip: '2000', city: 'Sydney', province: 'NSW' },
        GQ: { zip: '00000', city: 'Malabo', province: 'BI' },
        ET: { zip: '00000', city: 'Addis Ababa', province: 'AA' },
        AF: { zip: '1001', city: 'Kabul', province: 'KAB' },
        AX: { zip: '22100', city: 'Mariehamn', province: '' },
        AL: { zip: '1001', city: 'Tirana', province: 'TR' },
        DZ: { zip: '16000', city: 'Algiers', province: '16' },
        UM: { zip: '00000', city: 'Wake Island', province: '' },
        AD: { zip: 'AD500', city: 'Andorra la Vella', province: '07' },
        AO: { zip: '00000', city: 'Luanda', province: 'LUA' },
        AI: { zip: 'AI-2640', city: 'The Valley', province: '' },
        AG: { zip: '00000', city: "St. John's", province: '03' },
        AR: { zip: 'C1000', city: 'Buenos Aires', province: 'C' },
        AM: { zip: '0001', city: 'Yerevan', province: 'ER' },
        AW: { zip: '00000', city: 'Oranjestad', province: '' },
        AZ: { zip: 'AZ1000', city: 'Baku', province: 'BA' },
        BS: { zip: '00000', city: 'Nassau', province: 'NP' },
        BH: { zip: '00000', city: 'Manama', province: '13' },
        BD: { zip: '1000', city: 'Dhaka', province: '13' },
        BB: { zip: 'BB11000', city: 'Bridgetown', province: '11' },
        BY: { zip: '220000', city: 'Minsk', province: 'MI' },
        BZ: { zip: '00000', city: 'Belmopan', province: 'CY' },
        BJ: { zip: '00000', city: 'Porto-Novo', province: 'OU' },
        BM: { zip: 'HM 12', city: 'Hamilton', province: 'HA' },
        BT: { zip: '11001', city: 'Thimphu', province: '15' },
        BO: { zip: '00000', city: 'La Paz', province: 'L' },
        BA: { zip: '71000', city: 'Sarajevo', province: 'BIH' },
        BW: { zip: '00000', city: 'Gaborone', province: 'SE' },
        BR: { zip: '20000-000', city: 'Rio de Janeiro', province: 'RJ' },
        VG: { zip: 'VG1110', city: 'Road Town', province: '' },
        IO: { zip: 'BBND 1ZZ', city: 'Diego Garcia', province: '' },
        BN: { zip: 'BE1418', city: 'Bandar Seri Begawan', province: 'BE' },
        BG: { zip: '1000', city: 'Sofia', province: '22' },
        BF: { zip: '00000', city: 'Ouagadougou', province: 'KAD' },
        BI: { zip: '00000', city: 'Bujumbura', province: 'BM' },
        CV: { zip: '00000', city: 'Praia', province: 'S' },
        CL: { zip: '8320000', city: 'Santiago', province: 'RM' },
        CN: { zip: '100000', city: 'Beijing', province: 'BJ' },
        CK: { zip: '00000', city: 'Avarua', province: '' },
        CR: { zip: '10101', city: 'San José', province: 'SJ' },
        CI: { zip: '00000', city: 'Abidjan', province: 'AB' },
        CW: { zip: '00000', city: 'Willemstad', province: '' },
        DK: { zip: '1050', city: 'Copenhagen', province: '84' },
        DM: { zip: '00000', city: 'Roseau', province: '02' },
        DO: { zip: '10101', city: 'Santo Domingo', province: '01' },
        DJ: { zip: '00000', city: 'Djibouti', province: 'DJ' },
        EC: { zip: '170101', city: 'Quito', province: 'P' },
        SV: { zip: '00000', city: 'San Salvador', province: 'SS' },
        ER: { zip: '00000', city: 'Asmara', province: 'MA' },
        EE: { zip: '10111', city: 'Tallinn', province: '37' },
        SZ: { zip: 'H100', city: 'Mbabane', province: 'HH' },
        FO: { zip: '100', city: 'Tórshavn', province: '' },
        FK: { zip: 'FIQQ 1ZZ', city: 'Stanley', province: '' },
        FJ: { zip: '00000', city: 'Suva', province: 'C' },
        FI: { zip: '00100', city: 'Helsinki', province: '18' },
        GF: { zip: '97300', city: 'Cayenne', province: '973' },
        PF: { zip: '98713', city: 'Papeete', province: '987' },
        TF: { zip: '98400', city: 'Port-aux-Français', province: '' },
        GA: { zip: '00000', city: 'Libreville', province: '1' },
        GM: { zip: '00000', city: 'Banjul', province: 'B' },
        GE: { zip: '0100', city: 'Tbilisi', province: 'TB' },
        GH: { zip: 'GA184', city: 'Accra', province: 'AA' },
        GI: { zip: 'GX11 1AA', city: 'Gibraltar', province: '' },
        GD: { zip: '00000', city: "St. George's", province: '04' },
        GR: { zip: '104 31', city: 'Athens', province: 'I' },
        GL: { zip: '3900', city: 'Nuuk', province: 'GL' },
        GP: { zip: '97100', city: 'Basse-Terre', province: '971' },
        GT: { zip: '01001', city: 'Guatemala City', province: 'GT' },
        GG: { zip: 'GY1 1AA', city: 'St. Peter Port', province: '' },
        GN: { zip: '00000', city: 'Conakry', province: 'C' },
        GW: { zip: '00000', city: 'Bissau', province: 'BS' },
        GY: { zip: '00000', city: 'Georgetown', province: 'DE' },
        HT: { zip: '00000', city: 'Port-au-Prince', province: 'OU' },
        HN: { zip: '00000', city: 'Tegucigalpa', province: 'FM' },
        IN: { zip: '110001', city: 'New Delhi', province: 'DL' },
        ID: { zip: '10110', city: 'Jakarta', province: 'JK' },
        IQ: { zip: '10001', city: 'Baghdad', province: 'BG' },
        IE: { zip: 'D02 AF30', city: 'Dublin', province: 'D' },
        IS: { zip: '101', city: 'Reykjavik', province: '1' },
        IM: { zip: 'IM1 1AA', city: 'Douglas', province: '' },
        IL: { zip: '9100001', city: 'Jerusalem', province: 'JM' },
        JM: { zip: '00000', city: 'Kingston', province: '01' },
        JP: { zip: '100-0001', city: 'Tokyo', province: '13' },
        YE: { zip: '00000', city: 'Sana\'a', province: 'SA' },
        JE: { zip: 'JE1 1AA', city: 'Saint Helier', province: '' },
        JO: { zip: '11118', city: 'Amman', province: 'AM' },
        KY: { zip: 'KY1-1100', city: 'George Town', province: '' },
        KH: { zip: '12000', city: 'Phnom Penh', province: '12' },
        CM: { zip: '00000', city: 'Yaoundé', province: 'CE' },
        BQ: { zip: '00000', city: 'Kralendijk', province: '' },
        KZ: { zip: '010000', city: 'Astana', province: 'AST' },
        QA: { zip: '00000', city: 'Doha', province: 'DA' },
        KE: { zip: '00100', city: 'Nairobi', province: '110' },
        KG: { zip: '720000', city: 'Bishkek', province: 'C' },
        KI: { zip: '00000', city: 'Tarawa', province: 'G' },
        CC: { zip: '6799', city: 'West Island', province: '' },
        CO: { zip: '110111', city: 'Bogotá', province: 'DC' },
        KM: { zip: '00000', city: 'Moroni', province: 'G' },
        CG: { zip: '00000', city: 'Brazzaville', province: 'BZV' },
        CD: { zip: '00000', city: 'Kinshasa', province: 'KN' },
        XK: { zip: '10000', city: 'Pristina', province: 'XK' },
        HR: { zip: '10000', city: 'Zagreb', province: '21' },
        KW: { zip: '00000', city: 'Kuwait City', province: 'KU' },
        LA: { zip: '01000', city: 'Vientiane', province: 'VT' },
        LS: { zip: '100', city: 'Maseru', province: 'A' },
        LV: { zip: 'LV-1010', city: 'Riga', province: 'RIX' },
        LB: { zip: '00000', city: 'Beirut', province: 'BA' },
        LR: { zip: '00000', city: 'Monrovia', province: 'MO' },
        LY: { zip: '00000', city: 'Tripoli', province: 'TB' },
        LI: { zip: '9490', city: 'Vaduz', province: '' },
        LT: { zip: '01103', city: 'Vilnius', province: 'VL' },
        LU: { zip: 'L-1012', city: 'Luxembourg', province: 'L' },
        MG: { zip: '101', city: 'Antananarivo', province: 'T' },
        MW: { zip: '00000', city: 'Lilongwe', province: 'C' },
        MY: { zip: '50000', city: 'Kuala Lumpur', province: '14' },
        MV: { zip: '20026', city: 'Malé', province: 'MLE' },
        ML: { zip: '00000', city: 'Bamako', province: 'BKO' },
        MT: { zip: 'VLT 1000', city: 'Valletta', province: '01' },
        MA: { zip: '10000', city: 'Rabat', province: 'RAB' },
        MQ: { zip: '97200', city: 'Fort-de-France', province: '972' },
        MR: { zip: '00000', city: 'Nouakchott', province: 'NKC' },
        MU: { zip: '00000', city: 'Port Louis', province: 'PL' },
        YT: { zip: '97600', city: 'Mamoudzou', province: '976' },
        MX: { zip: '06000', city: 'Mexico City', province: 'CDMX' },
        MC: { zip: '98000', city: 'Monaco', province: '' },
        MN: { zip: '14200', city: 'Ulaanbaatar', province: '1' },
        ME: { zip: '81000', city: 'Podgorica', province: '15' },
        MS: { zip: 'MSR1110', city: 'Plymouth', province: '' },
        MZ: { zip: '00000', city: 'Maputo', province: 'MPM' },
        MM: { zip: '11181', city: 'Naypyidaw', province: '18' },
        NA: { zip: '00000', city: 'Windhoek', province: 'KH' },
        NR: { zip: '00000', city: 'Yaren', province: '14' },
        NP: { zip: '44600', city: 'Kathmandu', province: '3' },
        NC: { zip: '98800', city: 'Nouméa', province: '988' },
        NZ: { zip: '1010', city: 'Auckland', province: 'AUK' },
        NI: { zip: '00000', city: 'Managua', province: 'MN' },
        NE: { zip: '00000', city: 'Niamey', province: '8' },
        NG: { zip: '00000', city: 'Abuja', province: 'FC' },
        NU: { zip: '00000', city: 'Alofi', province: '' },
        MK: { zip: '1000', city: 'Skopje', province: '85' },
        NF: { zip: '2899', city: 'Kingston', province: '' },
        NO: { zip: '0001', city: 'Oslo', province: '03' },
        OM: { zip: '00000', city: 'Muscat', province: 'MA' },
        PK: { zip: '44000', city: 'Islamabad', province: 'IS' },
        PS: { zip: '00000', city: 'Ramallah', province: 'WE' },
        PA: { zip: '00000', city: 'Panama City', province: '8' },
        PG: { zip: '00000', city: 'Port Moresby', province: 'NCD' },
        PY: { zip: '00000', city: 'Asunción', province: 'ASU' },
        PE: { zip: '15001', city: 'Lima', province: 'LMA' },
        PH: { zip: '1000', city: 'Manila', province: 'NCR' },
        PN: { zip: 'PCRN 1ZZ', city: 'Adamstown', province: '' },
        PT: { zip: '1000-001', city: 'Lisbon', province: '11' },
        MD: { zip: '2001', city: 'Chișinău', province: 'CU' },
        RE: { zip: '97400', city: 'Saint-Denis', province: '974' },
        RW: { zip: '00000', city: 'Kigali', province: '01' },
        RO: { zip: '010011', city: 'Bucharest', province: 'B' },
        RU: { zip: '101000', city: 'Moscow', province: 'MOW' },
        SB: { zip: '00000', city: 'Honiara', province: 'CT' },
        ZM: { zip: '10101', city: 'Lusaka', province: '09' },
        WS: { zip: '00000', city: 'Apia', province: 'AA' },
        SM: { zip: '47890', city: 'San Marino', province: '07' },
        ST: { zip: '00000', city: 'São Tomé', province: 'P' },
        SA: { zip: '11564', city: 'Riyadh', province: '01' },
        SE: { zip: '111 20', city: 'Stockholm', province: 'AB' },
        SN: { zip: '00000', city: 'Dakar', province: 'DK' },
        RS: { zip: '11000', city: 'Belgrade', province: '00' },
        SC: { zip: '00000', city: 'Victoria', province: '20' },
        SL: { zip: '00000', city: 'Freetown', province: 'W' },
        ZW: { zip: '00000', city: 'Harare', province: 'HA' },
        SG: { zip: '018956', city: 'Singapore', province: '' },
        SX: { zip: '00000', city: 'Philipsburg', province: '' },
        SK: { zip: '811 01', city: 'Bratislava', province: 'BL' },
        SI: { zip: '1000', city: 'Ljubljana', province: '061' },
        SO: { zip: '00000', city: 'Mogadishu', province: 'BN' },
        HK: { zip: '000000', city: 'Hong Kong', province: '' },
        SJ: { zip: '9170', city: 'Longyearbyen', province: '' },
        LK: { zip: '00100', city: 'Colombo', province: '1' },
        BL: { zip: '97133', city: 'Gustavia', province: '' },
        SH: { zip: 'STHL 1ZZ', city: 'Jamestown', province: '' },
        KN: { zip: '00000', city: 'Basseterre', province: 'K' },
        LC: { zip: '00000', city: 'Castries', province: '06' },
        MF: { zip: '97150', city: 'Marigot', province: '' },
        PM: { zip: '97500', city: 'Saint-Pierre', province: '' },
        VC: { zip: 'VC0100', city: 'Kingstown', province: '01' },
        SD: { zip: '11111', city: 'Khartoum', province: 'KH' },
        ZA: { zip: '0001', city: 'Pretoria', province: 'GP' },
        GS: { zip: 'SIQQ 1ZZ', city: 'King Edward Point', province: '' },
        KR: { zip: '03051', city: 'Seoul', province: '11' },
        SS: { zip: '00000', city: 'Juba', province: 'CE' },
        SR: { zip: '00000', city: 'Paramaribo', province: 'PM' },
        TJ: { zip: '734000', city: 'Dushanbe', province: 'DU' },
        TW: { zip: '100', city: 'Taipei', province: 'TPE' },
        TZ: { zip: '00000', city: 'Dodoma', province: '03' },
        TH: { zip: '10100', city: 'Bangkok', province: '10' },
        TL: { zip: '00000', city: 'Dili', province: 'DI' },
        TG: { zip: '00000', city: 'Lomé', province: 'M' },
        TK: { zip: '00000', city: 'Fakaofo', province: '' },
        TO: { zip: '00000', city: 'Nuku\'alofa', province: '04' },
        TT: { zip: '00000', city: 'Port of Spain', province: 'POS' },
        TD: { zip: '00000', city: 'N\'Djamena', province: 'ND' },
        CZ: { zip: '110 00', city: 'Prague', province: '10' },
        TR: { zip: '34000', city: 'Istanbul', province: '34' },
        TN: { zip: '1000', city: 'Tunis', province: '11' },
        TM: { zip: '744000', city: 'Ashgabat', province: 'S' },
        TC: { zip: 'TKCA 1ZZ', city: 'Cockburn Town', province: '' },
        TV: { zip: '00000', city: 'Funafuti', province: 'FUN' },
        UG: { zip: '00000', city: 'Kampala', province: 'C' },
        UA: { zip: '01001', city: 'Kyiv', province: '30' },
        HU: { zip: '1011', city: 'Budapest', province: 'BU' },
        UY: { zip: '11000', city: 'Montevideo', province: 'MO' },
        UZ: { zip: '100000', city: 'Tashkent', province: 'TK' },
        VU: { zip: '00000', city: 'Port Vila', province: 'SEE' },
        VA: { zip: '00120', city: 'Vatican City', province: '' },
        VE: { zip: '1010', city: 'Caracas', province: 'A' },
        AE: { zip: '00000', city: 'Abu Dhabi', province: 'AZ' },
        VN: { zip: '100000', city: 'Hanoi', province: 'HN' },
        WF: { zip: '98600', city: 'Mata-Utu', province: '' },
        CX: { zip: '6798', city: 'Flying Fish Cove', province: '' },
        EH: { zip: '00000', city: 'El Aaiún', province: '' },
        CF: { zip: '00000', city: 'Bangui', province: 'BGF' },
        CY: { zip: '1011', city: 'Nicosia', province: '01' },
      };

      const address = defaultAddresses[countryCode];

      if (!address) {
        return {
          country: countryCode,
          zip: '00000',
          province: '',
        };
      }

      const result = {
        country: countryCode,
        zip: address.zip,
        province: address.province || '',
      };

      if (address.city) {
        result.city = address.city;
      }

      return result;
    }

    getPriceValue(rate) {
      const price = rate.price ?? rate.amount ?? rate.cost ?? 0;
      
      if (typeof price === 'string') {
        const cleaned = price.replace(/[€$£,\s]/g, '');
        return parseFloat(cleaned) || 0;
      }
      
      return typeof price === 'number' ? price : 0;
    }

    getPriceInCents(rate) {
      const price = rate.price ?? rate.amount ?? rate.cost ?? '0';
      const priceString = typeof price === 'number' 
        ? price.toFixed(2) 
        : String(price);
      
      return `${priceString.replace('.', ',')} €`;
    }

    formatMoney(cents) {
      const euros = cents / 100;
      return new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(euros);
    }

    setShippingCost(value) {
      if (this.shippingCostElement) {
        this.shippingCostElement.textContent = value;
      }
    }
  }

  let shippingInstance = null;

  const initShipping = () => {
    if (!shippingInstance) {
      shippingInstance = new CartShipping();
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShipping);
  } else {
    setTimeout(initShipping, INIT_DELAY);
  }
})();
